<script lang="ts">
	import EssayLayout from '$lib/components/EssayLayout.svelte';
	import { canonicalUrl, jsonLdGraph, ogImageUrl, pageTitle } from '$lib/seo';
	import TheQuietMorning from './_essays/TheQuietMorning.svelte';
	import { onMount } from 'svelte';
	import { browser } from '$app/environment';
	import { trackBlogRead } from '$lib/analytics';

	let { data } = $props();
	const meta = $derived(data.seo!);
	const post = $derived(data.post);

	// Reading-event: 50% scroll + 30s dwell. Fires once.
	let fired = false;
	let dwellStart = 0;
	onMount(() => {
		if (!browser || post.status !== 'published') return;
		dwellStart = Date.now();

		function maybeFire() {
			if (fired) return;
			const dwell = Date.now() - dwellStart;
			const docHeight = document.documentElement.scrollHeight - window.innerHeight;
			const pct = docHeight > 0 ? window.scrollY / docHeight : 0;
			if (dwell >= 30_000 && pct >= 0.5) {
				fired = true;
				trackBlogRead(post.slug, Math.round(dwell / 1000));
				window.removeEventListener('scroll', maybeFire);
			}
		}

		window.addEventListener('scroll', maybeFire, { passive: true });
		const fallback = setTimeout(maybeFire, 35_000);
		return () => {
			window.removeEventListener('scroll', maybeFire);
			clearTimeout(fallback);
		};
	});
</script>

<svelte:head>
	<title>{pageTitle(meta)}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={canonicalUrl(meta)} />
	<meta property="og:type" content={post.status === 'published' ? 'article' : 'website'} />
	<meta property="og:url" content={canonicalUrl(meta)} />
	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:image" content={ogImageUrl(meta)} />
	<meta name="twitter:card" content="summary_large_image" />
	{#if post.status === 'published' && post.publishedAt}
		<meta property="article:published_time" content={post.publishedAt} />
	{/if}
	{#if post.status === 'scheduled'}
		<meta name="robots" content="noindex, follow" />
	{/if}
	{@html `<script type="application/ld+json">${jsonLdGraph(meta)}</script>`}
</svelte:head>

<div class="container-wide">
	<EssayLayout
		title={post.title}
		lede={post.description}
		publishedAt={post.publishedAt}
		readingTime={post.readingTimeMinutes}
		status={post.status}
	>
		{#if post.status === 'scheduled'}
			<p>
				This note is being written. It will land soon. If you would like an early read,
				<a href="/#design-partners">apply to be one of five</a> — design partners see drafts.
			</p>
			<p class="placeholder-sign">— Ekavi.</p>
		{:else if post.slug === 'the-quiet-morning'}
			<TheQuietMorning />
		{:else}
			<p>This letter is in draft.</p>
		{/if}
	</EssayLayout>

	<nav class="essay-nav" aria-label="Essay navigation">
		<a class="essay-nav-link" href="/notes/">← back to notes</a>
		<a class="essay-nav-link" href="/#design-partners"
			>apply to be one of five <span class="cta-arrow" aria-hidden="true">→</span></a
		>
	</nav>
</div>

<style>
	.placeholder-sign {
		font-family: var(--ekavi-font-ui);
		font-style: italic;
		color: var(--ekavi-ink-2);
		margin-top: var(--ekavi-space-9);
	}
	.essay-nav {
		display: flex;
		justify-content: space-between;
		gap: var(--ekavi-space-6);
		flex-wrap: wrap;
		margin: var(--ekavi-space-12) 0 var(--ekavi-space-24);
		padding: var(--ekavi-space-6) 0;
		border-top: var(--ekavi-stroke-hairline);
		border-bottom: var(--ekavi-stroke-hairline);
	}
	.essay-nav-link {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		letter-spacing: var(--ekavi-track-caps);
		text-transform: uppercase;
		color: var(--ekavi-ink-2);
		border-bottom-color: transparent;
	}
	.essay-nav-link:hover {
		color: var(--ekavi-charcoal);
		border-bottom-color: var(--ekavi-charcoal);
	}
</style>
